import React, { useState, useEffect, Suspense } from 'react'; // Added Suspense
import { collection, doc, getDocs, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs'; // Added Tabs
import { useToast } from '../../hooks/use-toast';
import { LoadingSpinner } from '../../components/ui/loading-spinner';
import 'react-quill/dist/quill.snow.css'; // Import Quill styles

// Dynamically import ReactQuill to handle cases where it might not be installed
// or to improve initial load time.
const ReactQuill = React.lazy(() => import('react-quill'));

const EmailTemplateAdmin = () => {
  const { toast } = useToast();
  
  // State
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [subject, setSubject] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('view'); // view, edit, create
  const [editorViewMode, setEditorViewMode] = useState('html'); // 'html' or 'wysiwyg'

  // Inject styles for Quill editor text visibility
  useEffect(() => {
    const styleId = 'quill-custom-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `
        .ql-editor {
          color: #333; /* Dark gray text for better readability */
          background-color: #fff; /* Ensure editor area itself is white */
        }
        .ql-editor.ql-blank::before {
          color: #aaa; /* Lighter gray for placeholder text */
          font-style: normal; /* Ensure placeholder is not italic if base is */
        }
        .quill-view-mode .ql-editor {
          color: #333; /* Ensure read-only view also has dark text */
        }
      `;
      document.head.appendChild(style);
    }
    // Optional: Cleanup function to remove the style when the component unmounts
    // return () => {
    //   const styleElement = document.getElementById(styleId);
    //   if (styleElement) {
    //     document.head.removeChild(styleElement);
    //   }
    // };
  }, []);
  
  // Fetch templates
  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      try {
        const templatesSnapshot = await getDocs(collection(db, 'emailTemplates'));
        const templatesList = templatesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setTemplates(templatesList);
      } catch (error) {
        console.error('Error fetching templates:', error);
        toast({
          title: 'Error',
          description: 'Failed to load email templates',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchTemplates();
  }, [toast]);
  
  // Select template
  const handleSelectTemplate = async (id) => {
    if (id) {
      setLoading(true);
      try {
        const templateDoc = await getDoc(doc(db, 'emailTemplates', id));
        if (templateDoc.exists()) {
          const data = templateDoc.data();
          setSelectedTemplate(data);
          setTemplateId(id);
          setTemplateName(data.name || '');
          setSubject(data.subject || '');
          setHtmlContent(data.html || '');
          setVariables(data.variables || []);
          setMode('view');
        }
      } catch (error) {
        console.error('Error loading template:', error);
        toast({
          title: 'Error',
          description: 'Failed to load template details',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    } else {
      setSelectedTemplate(null);
      setTemplateId('');
      setTemplateName('');
      setSubject('');
      setHtmlContent('');
      setVariables([]);
    }
  };
  
  // Create/Update template
  const handleSaveTemplate = async () => {
    if (!templateName || !subject || !htmlContent) {
      toast({
        title: 'Missing Fields',
        description: 'Template name, subject, and HTML content are required',
        variant: 'warning'
      });
      return;
    }
    
    setSaving(true);
    
    try {
      // Extract variables from HTML content using regex
      const extractedVariables = [];
      const regex = /{{(\w+)}}/g;
      let match;
      while ((match = regex.exec(htmlContent)) !== null) {
        if (!extractedVariables.includes(match[1])) {
          extractedVariables.push(match[1]);
        }
      }
      
      const templateData = {
        name: templateName,
        subject,
        html: htmlContent,
        variables: extractedVariables,
        updatedAt: new Date()
      };
      
      if (mode === 'edit' && templateId) {
        // Update existing
        await setDoc(doc(db, 'emailTemplates', templateId), templateData, { merge: true });
        toast({
          title: 'Template Updated',
          description: `Template "${templateName}" has been updated`,
          variant: 'success'
        });
      } else {
        // Create new
        const newId = templateId || templateName.toLowerCase().replace(/\s+/g, '_');
        await setDoc(doc(db, 'emailTemplates', newId), {
          ...templateData,
          createdAt: new Date()
        });
        setTemplateId(newId);
        toast({
          title: 'Template Created',
          description: `Template "${templateName}" has been created`,
          variant: 'success'
        });
      }
      
      // Refresh templates list
      const templatesSnapshot = await getDocs(collection(db, 'emailTemplates'));
      const templatesList = templatesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTemplates(templatesList);
      setMode('view');
    } catch (error) {
      console.error('Error saving template:', error);
      toast({
        title: 'Error',
        description: 'Failed to save template',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };
  
  // Delete template
  const handleDeleteTemplate = async () => {
    if (!templateId) return;
    
    if (!window.confirm(`Are you sure you want to delete the template "${templateName}"?`)) {
      return;
    }
    
    setSaving(true);
    
    try {
      await deleteDoc(doc(db, 'emailTemplates', templateId));
      toast({
        title: 'Template Deleted',
        description: `Template "${templateName}" has been deleted`,
        variant: 'success'
      });
      
      // Refresh templates list
      const templatesSnapshot = await getDocs(collection(db, 'emailTemplates'));
      const templatesList = templatesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTemplates(templatesList);
      
      // Clear form
      setSelectedTemplate(null);
      setTemplateId('');
      setTemplateName('');
      setSubject('');
      setHtmlContent('');
      setVariables([]);
      setMode('view');
    } catch (error) {
      console.error('Error deleting template:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete template',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  // Helper function to render the content of the template list card
  const renderTemplateListContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {templates.length === 0 ? (
          <p className="text-center text-gray-500 py-4">No templates found</p>
        ) : (
          templates.map((template) => (
            <div 
              key={template.id}
              className={`p-3 border rounded cursor-pointer hover:bg-gray-50 ${selectedTemplate && templateId === template.id ? 'bg-blue-50 border-blue-200' : ''}`}
              onClick={() => handleSelectTemplate(template.id)}
            >
              <p className="font-medium">{template.name || template.id}</p>
              <p className="text-sm text-gray-500 truncate">{template.subject}</p>
            </div>
          ))
        )}
      </div>
    );
  };
  
  const quillModules = {
    toolbar: [
      [{ 'header': '1'}, {'header': '2'}, { 'font': [] }],
      [{size: []}],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{'list': 'ordered'}, {'list': 'bullet'}, 
       {'indent': '-1'}, {'indent': '+1'}],
      ['link', 'image', 'video'],
      ['clean'],
      [{ 'color': [] }, { 'background': [] }], // Color and background color
      [{ 'align': [] }], // Text alignment
    ],
  };

  const quillFormats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image', 'video',
    'color', 'background', 'align' // Added color, background, align
  ];

  // Modules for read-only view (no toolbar)
  const quillViewModules = {
    toolbar: false,
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <h1 className="text-3xl font-bold mb-8">Email Template Management</h1>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Template List */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Email Templates</CardTitle>
            <CardDescription>Select a template to edit or create a new one</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <Button 
                onClick={() => {
                  setSelectedTemplate(null);
                  setTemplateId('');
                  setTemplateName('');
                  setSubject('');
                  setHtmlContent('');
                  setVariables([]);
                  setMode('create');
                }}
                className="w-full"
              >
                Create New Template
              </Button>
            </div>
            
            {renderTemplateListContent()} {/* Replaced inline conditional logic */}

          </CardContent>
        </Card>
        
        {/* Template Editor */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>
              {mode === 'create' ? 'Create New Template' : 
               mode === 'edit' ? 'Edit Template' : 
               selectedTemplate ? selectedTemplate.name || templateId : 'Template Details'}
            </CardTitle>
            <CardDescription>
              {mode === 'view' ? 'View template details or click Edit to make changes' : 'Enter template details'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="templateName">Template Name</Label>
                    <Input
                      id="templateName"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      placeholder="e.g., Welcome Email"
                      disabled={mode === 'view'}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="templateId">Template ID</Label>
                    <Input
                      id="templateId"
                      value={templateId}
                      onChange={(e) => setTemplateId(e.target.value)}
                      placeholder="e.g., welcome_email"
                      disabled={mode === 'view' || (mode === 'edit' && selectedTemplate)}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Used as the unique identifier for this template
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="subject">Email Subject</Label>
                    <Input
                      id="subject"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="Email Subject Line"
                      disabled={mode === 'view'}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="htmlContent">HTML Content</Label>
                    {mode === 'edit' || mode === 'create' ? (
                      <Tabs value={editorViewMode} onValueChange={setEditorViewMode} className="w-full mt-1">
                        <TabsList className="grid w-full grid-cols-2">
                          <TabsTrigger value="html">HTML Source</TabsTrigger>
                          <TabsTrigger value="wysiwyg">Visual Editor</TabsTrigger>
                        </TabsList>
                        <TabsContent value="html">
                          <Textarea
                            id="htmlContentSource" // Changed id to avoid conflict if htmlContent is used elsewhere
                            value={htmlContent}
                            onChange={(e) => setHtmlContent(e.target.value)}
                            placeholder="<h1>Hello {{userName}}!</h1><p>Your email content here...</p>"
                            rows={15}
                            className="font-mono text-sm mt-2"
                            required
                          />
                        </TabsContent>
                        <TabsContent value="wysiwyg">
                          <Suspense fallback={<div className="flex justify-center py-8"><LoadingSpinner /></div>}>
                            <ReactQuill
                              theme="snow"
                              value={htmlContent}
                              onChange={setHtmlContent}
                              modules={quillModules}
                              formats={quillFormats}
                              className="bg-white mt-2 min-h-[300px]" 
                              placeholder="Compose your email visually..."
                              readOnly={false} // Explicitly false for edit/create
                            />
                          </Suspense>
                           <p className="text-xs text-gray-500 mt-2">
                            Visual editor may alter complex HTML. Always review in HTML source mode if pasting from external tools.
                            Ensure variables like <code className="bg-muted p-1 rounded">{"{{variableName}}"}</code> are correctly maintained.
                          </p>
                        </TabsContent>
                      </Tabs>
                    ) : selectedTemplate ? (
                      // Visual preview for 'view' mode if a template is selected
                      <div className="mt-1 p-1 border rounded bg-gray-50">
                        <Suspense fallback={<div className="flex justify-center py-8"><LoadingSpinner /></div>}>
                          <ReactQuill
                            theme="snow" // Using "snow" theme but hiding toolbar via modules
                            value={htmlContent}
                            readOnly={true}
                            modules={quillViewModules} // Hide toolbar
                            formats={quillFormats} // Keep formats for correct rendering
                            className="bg-white min-h-[300px] border-none quill-view-mode" // Added class for potential specific styling
                          />
                        </Suspense>
                      </div>
                    ) : (
                      // Fallback for 'view' mode when no template is selected (e.g., initial load)
                      <Textarea
                        id="htmlContentView" // Changed id
                        value={htmlContent} // Will be empty or show placeholder
                        rows={15}
                        className="font-mono text-sm mt-1"
                        disabled 
                        readOnly 
                        placeholder="Select a template to view its content."
                      />
                    )}
                    {(mode === 'edit' || mode === 'create') && editorViewMode === 'html' && (
                       <p className="text-xs text-gray-500 mt-1">
                        Use {"{{variableName}}"} syntax for dynamic content.
                      </p>
                    )}
                  </div>
                  
                  {variables.length > 0 && (
                    <div>
                      <Label>Variables</Label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {variables.map((variable) => (
                          <div key={variable} className="bg-gray-100 px-2 py-1 rounded text-sm">
                            {`{{${variable}}}`}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex justify-between mt-6">
                  {mode === 'view' && selectedTemplate ? (
                    <>
                      <Button 
                        variant="outline" 
                        onClick={() => setMode('edit')}
                      >
                        Edit Template
                      </Button>
                      <Button 
                        variant="destructive" 
                        onClick={handleDeleteTemplate}
                        disabled={saving}
                      >
                        {saving ? <LoadingSpinner size="sm" /> : 'Delete Template'}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          if (selectedTemplate) {
                            handleSelectTemplate(templateId);
                          } else {
                            setSelectedTemplate(null);
                            setTemplateId('');
                            setTemplateName('');
                            setSubject('');
                            setHtmlContent('');
                            setVariables([]);
                          }
                          setMode('view');
                        }}
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleSaveTemplate}
                        disabled={saving}
                      >
                        {saving ? <LoadingSpinner size="sm" /> : 'Save Template'}
                      </Button>
                    </>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Help Card */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Template Variables Guide</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-4">Use these variables in your email templates:</p>
          <ul className="list-disc list-inside space-y-1">
            <li><code className="bg-muted p-1 rounded">{"{{userName}}"}</code> - Recipient's name (falls back to email username if not provided)</li>
            <li><code className="bg-muted p-1 rounded">{"{{userEmail}}"}</code> - Recipient's email address</li>
            <li><code className="bg-muted p-1 rounded">{"{{emailSubject}}"}</code> - Email subject (useful in template)</li>
            <li>Any custom variables added when sending the email will be available as <code className="bg-muted p-1 rounded">{"{{variableName}}"}</code></li>
          </ul>
          <p className="mt-4 text-sm text-gray-500">
            Variables are automatically detected from your HTML content when saving the template.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailTemplateAdmin;
